import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import Badge from 'components/badge/badge';
import MissionDetailTempWorker from 'pages/pages/mission/missionDetailTempWorker';
import Button from '../../../../button';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import Modalheader from '../../../components/header';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';

const normalizeSearch = (value) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();

const ModalMissionInviteCandidates = () => {
  const { toggle } = useModal();
  const {
    infoMission,
    searchTempWorkerInviteResult,
    setModalDataReturn,
    language,
  } = UseContext();
  const [tempWorkers, setTempWorkers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const token = localStorage.getItem('xsrfToken');
  const invitedUserIds = useMemo(
    () =>
      new Set(
        (searchTempWorkerInviteResult || [])
          .map((user) => user._id?.toString())
          .filter(Boolean)
      ),
    [searchTempWorkerInviteResult]
  );

  const displayedTempWorkers = useMemo(() => {
    const searchValue = normalizeSearch(search);
    if (!searchValue) return tempWorkers;
    return tempWorkers.filter((tempWorker) =>
      normalizeSearch(tempWorker.name).includes(searchValue)
    );
  }, [search, tempWorkers]);

  const selectableDisplayedIds = useMemo(
    () =>
      displayedTempWorkers
        .filter((tempWorker) => !invitedUserIds.has(tempWorker._id?.toString()))
        .map((tempWorker) => tempWorker._id),
    [displayedTempWorkers, invitedUserIds]
  );

  const allDisplayedSelected =
    selectableDisplayedIds.length > 0 &&
    selectableDisplayedIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    if (!infoMission?._id || !infoMission?.company?._id) return;

    setIsLoading(true);
    ApiDatabase.getTempWorkersRatingByMission(
      {
        token,
        idMission: infoMission._id,
        idCompany: infoMission.company._id,
        titleMission: infoMission.title,
        dateStart: infoMission.duration?.datestart,
        dateEnd: infoMission.duration?.dateend,
      },
      (response) => {
        setTempWorkers(response || []);
        setIsLoading(false);
      },
      () => {
        toast.error('Une erreur est survenue, veuillez réessayer plus tard.');
        setIsLoading(false);
      },
      { skipCache: true }
    );
  }, [infoMission, token]);

  const toggleTempWorker = (id) => {
    setSelectedIds((prevData) =>
      prevData.includes(id)
        ? prevData.filter((selectedId) => selectedId !== id)
        : [...prevData, id]
    );
  };

  const toggleAllDisplayed = () => {
    setSelectedIds((prevData) => {
      if (allDisplayedSelected) {
        return prevData.filter((id) => !selectableDisplayedIds.includes(id));
      }
      return [...new Set([...prevData, ...selectableDisplayedIds])];
    });
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0 || isConfirming) return;

    setIsConfirming(true);
    ApiDatabase.inviteUsersToMission(
      {
        token,
        missionId: infoMission._id,
        usersInvite: selectedIds.map((id) => ({ id_user: id })),
      },
      () => {
        toast.success('Les invitations ont été envoyées.');
        setModalDataReturn('mission invitations sent');
        setIsConfirming(false);
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || 'Une erreur est survenue.');
        setIsConfirming(false);
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Inviter un candidat
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-500">
            Sélectionnez un ou plusieurs candidats à inviter pour la{' '}
            {infoMission?.title} n°{infoMission?.id_mission}
          </p>
          <input
            type="text"
            className="w-full h-10 py-2 px-3 ft-b border rounded-lg text-sm"
            placeholder="Rechercher un candidat"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-col max-h-[480px] overflow-y-auto border rounded-lg">
            {isLoading ? (
              [1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="flex gap-4 px-5 py-4 border-b last:border-b-0"
                >
                  <div className="w-4 h-4 skeleton-bar my-auto" />
                  <div className="flex-1">
                    <div className="w-40 h-4 skeleton-bar" />
                    <div className="w-64 h-3 skeleton-bar mt-2" />
                  </div>
                </div>
              ))
            ) : displayedTempWorkers.length > 0 ? (
              displayedTempWorkers.map((tempWorker, index) => {
                const id = tempWorker._id;
                const isAlreadyInvited = invitedUserIds.has(id?.toString());

                return (
                  <MissionDetailTempWorker
                    key={id}
                    index={index}
                    tempWorker={tempWorker}
                    missionDateStart={infoMission?.duration?.datestart}
                    missionDateEnd={infoMission?.duration?.dateend}
                    isLast={index === displayedTempWorkers.length - 1}
                    onSelect={toggleTempWorker}
                    isSelected={selectedIds.includes(id)}
                    isSelectionDisabled={isAlreadyInvited}
                    statusBadge={
                      isAlreadyInvited && (
                        <Badge type="PRIMARY">
                          {language.getValue('inviteCandidates.alreadyInvited', '')}
                        </Badge>
                      )
                    }
                  />
                );
              })
            ) : (
              <div className="py-10 text-center text-sm text-gray-500">
                Aucun candidat disponible.
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex items-center justify-between w-full">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="checkbox-primary"
              checked={allDisplayedSelected}
              disabled={selectableDisplayedIds.length === 0 || isConfirming}
              onChange={toggleAllDisplayed}
            />
            Tout sélectionner
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="SM"
              color="SECONDARY"
              disabled={isConfirming}
              onClick={toggle}
            >
              Annuler
            </Button>
            <Button
              size="SM"
              color={selectedIds.length === 0 || isConfirming ? 'DISABLED' : 'PRIMARY'}
              disabled={selectedIds.length === 0 || isConfirming}
              onClick={handleConfirm}
            >
              {isConfirming ? 'Confirmation ...' : 'Confirmer'}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </>
  );
};

export default ModalMissionInviteCandidates;
